import { useId, useRef, useState } from "react";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { HeldChargeResolution, type ClearChargeRequest } from "@models/billing";
import type { ResolutionDetails } from "../_helpers/resolutionDetails";

type Resolution = ClearChargeRequest["resolution"];

interface ClearChargeDialogProps {
    // What the admin is clearing, e.g. `Downtown — Acme Dispensary`.
    subjectName: string;
    // The row names a held transaction. A held charge has its own two exits
    // (released / voided), so Settled is not offered for it; with none held
    // there is nothing to release, so Released is not offered (#439). The
    // page reloads the list after a refusal, so this follows the row while
    // the dialog is open; the API still refuses either in a race.
    isHeld: boolean;
    // The line under each choice, for this kind of charge: what clearing it
    // that way does (see _helpers/resolutionDetails).
    resolutionDetails: ResolutionDetails;
    isProcessing: boolean;
    // Server-side failure (notably the released-with-no-held-record 400),
    // rendered inline so the dialog keeps its context. null = no error.
    errorMessage: string | null;
    onConfirm: (request: ClearChargeRequest) => void;
    onClose: () => void;
    // Invoked when the admin changes the resolution or the transaction id: the
    // owner clears the stale inline error so the old failure doesn't
    // implicate the new, not-yet-submitted choice.
    onErrorClear: () => void;
}

// Each choice's title. Voided is always offered; the second choice is
// Released for a held charge and Settled otherwise.
const RESOLUTION_TITLES: Record<Resolution, string> = {
    [HeldChargeResolution.Voided]: "Voided at the vendor",
    [HeldChargeResolution.Released]: "Released — the charge settled",
    [HeldChargeResolution.Settled]: "It went through (settled)",
};

// Authorize.Net transaction ids are numeric. The API refuses zero and parses
// the id into a long, so at most 19 digits.
const MAX_TRANSACTION_ID_DIGITS = 19;

const transactionIdError = (transactionId: string): string | null => {
    if (transactionId === "") {
        return "Enter the Authorize.Net transaction ID.";
    }
    if (!/^\d+$/.test(transactionId)) {
        return "The transaction ID is digits only.";
    }
    if (/^0+$/.test(transactionId)) {
        return "The transaction ID can't be all zeros.";
    }
    if (transactionId.length > MAX_TRANSACTION_ID_DIGITS) {
        return `The transaction ID is at most ${MAX_TRANSACTION_ID_DIGITS} digits.`;
    }

    return null;
};

// Void-vs-release resolution choice for the admin clear flow (#382), plus
// Settled (#439) for an unconfirmed charge found settled at the vendor. Voided
// is the default: the runbook is VOID-first — an admin records a payment only
// when they have confirmed at Authorize.Net that the charge actually settled.
export const ClearChargeDialog = ({
    subjectName,
    isHeld,
    resolutionDetails,
    isProcessing,
    errorMessage,
    onConfirm,
    onClose,
    onErrorClear,
}: ClearChargeDialogProps) => {
    const [picked, setPicked] = useState<Resolution>(
        HeldChargeResolution.Voided,
    );
    const [transactionId, setTransactionId] = useState("");
    // Set by a refused Confirm; dropped as soon as the id or the choice
    // changes.
    const [fieldError, setFieldError] = useState<string | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const transactionIdId = useId();
    const transactionIdErrorId = useId();
    // Escape, backdrop and Cancel are all suppressed while isProcessing,
    // because a mid-flight close would silently drop the clear's outcome — the
    // inline 400's operator instructions would land on a closed dialog.
    // TODO(post-MVP): fold into a shared @common ConfirmDialog — the chassis
    // now lives in useDialogFocusTrap; the markup is still per-dialog.
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
    });

    const offered: Resolution[] = [
        HeldChargeResolution.Voided,
        isHeld ? HeldChargeResolution.Released : HeldChargeResolution.Settled,
    ];
    // A pick the row no longer offers (it turned held, or stopped being held)
    // falls back to Voided, never to a hidden choice.
    const resolution = offered.includes(picked)
        ? picked
        : HeldChargeResolution.Voided;

    const choose = (next: Resolution) => {
        onErrorClear();
        setFieldError(null);
        setPicked(next);
    };

    // Only Settled carries the id: the API refuses one with anything else.
    const handleConfirm = () => {
        if (resolution !== HeldChargeResolution.Settled) {
            onConfirm({ resolution });
            return;
        }

        const trimmed = transactionId.trim();
        const error = transactionIdError(trimmed);
        if (error) {
            setFieldError(error);
            return;
        }

        onConfirm({ resolution, transactionId: trimmed });
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="w-full max-w-md rounded-lg bg-base-100 p-6 text-neutral shadow-xl focus:outline-none"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id={titleId} className="mb-2 text-lg font-bold">
                    Clear unconfirmed charge — {subjectName}
                </h2>
                <p className="mb-4 text-sm">
                    How was the charge resolved at Authorize.Net?
                </p>

                <div className="mb-4 flex flex-col gap-3">
                    {offered.map((option) => (
                        <label
                            key={option}
                            className="flex cursor-pointer items-start gap-3"
                        >
                            <input
                                type="radio"
                                name="resolution"
                                className="radio radio-primary mt-1"
                                checked={resolution === option}
                                disabled={isProcessing}
                                onChange={() => choose(option)}
                            />
                            <span>
                                <span className="block font-semibold">
                                    {RESOLUTION_TITLES[option]}
                                </span>
                                <span className="block text-sm text-neutral/70">
                                    {resolutionDetails[option]}
                                </span>
                            </span>
                        </label>
                    ))}

                    {resolution === HeldChargeResolution.Settled && (
                        <div className="ml-9">
                            <label
                                htmlFor={transactionIdId}
                                className="mb-1 block text-sm font-semibold"
                            >
                                Authorize.Net transaction ID
                            </label>
                            {/* No <form>: Enter here never confirms. */}
                            <input
                                id={transactionIdId}
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                className="input input-bordered input-sm w-full"
                                value={transactionId}
                                disabled={isProcessing}
                                aria-invalid={fieldError ? true : undefined}
                                aria-describedby={
                                    fieldError
                                        ? transactionIdErrorId
                                        : undefined
                                }
                                onChange={(event) => {
                                    onErrorClear();
                                    setFieldError(null);
                                    setTransactionId(event.target.value);
                                }}
                            />
                            {fieldError && (
                                <p
                                    id={transactionIdErrorId}
                                    className="mt-1 text-sm text-error"
                                >
                                    {fieldError}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {errorMessage && (
                    <p role="alert" className="mb-4 text-sm text-error">
                        {errorMessage}
                    </p>
                )}

                {/* Safe back-out first, and marked: the focus trap focuses it
                    on open and after processing, so a stray Enter never
                    clears (#452). */}
                <div className="flex justify-end gap-2">
                    <span data-dialog-initial-focus="" className="contents">
                        <Button
                            color={ComponentColor.Neutral}
                            size={ComponentSize.SM}
                            disabled={isProcessing}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </span>
                    <Button
                        color={ComponentColor.Primary}
                        size={ComponentSize.SM}
                        loading={isProcessing}
                        onClick={handleConfirm}
                    >
                        Clear charge
                    </Button>
                </div>
            </div>
        </div>
    );
};
